import React, { memo, useCallback, useEffect, useState } from 'react';

import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import i18n from '../messages.json';

/**
 * Custom image override + Include-image gate.
 *
 * - URL input lets the editor paste a different image URL. Empty value falls
 *   through to the article's image_url; non-empty + invalid blocks Save/Preview.
 * - Thumbnail preview shows the effective URL; on <img> error, swap to a
 *   "Couldn't load image" fallback (tracked via local thumbnailFailed state,
 *   reset whenever the effective URL changes).
 * - "Include image (optional)" checkbox is the single send-or-not gate; it's
 *   disabled when there's no effective image. Hover/focus tooltip explains it.
 *
 * Props
 *   value:                  currentMessage.imageUrl.value
 *   onUrlChange:            (event) => void — patches imageUrl.value
 *   isCustomImageUrlValid:  boolean
 *   thumbnailSrc:           string — effective URL (override OR article.image_url)
 *   isIncludeImage:         boolean
 *   onIncludeImageChange:   (event) => void
 */
const CustomImageField = ({
  value,
  onUrlChange,
  isCustomImageUrlValid,
  thumbnailSrc,
  isIncludeImage,
  onIncludeImageChange,
}) => {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const hasEffective = thumbnailSrc !== '';

  useEffect(() => {
    setThumbnailFailed(false);
  }, [thumbnailSrc]);

  const handleImageError = useCallback(() => setThumbnailFailed(true), []);

  return (
    <>
      <section className='image-override-section'>
        <div className='image-override-section__heading'>
          {i18n.ink.notification.imageUrl.sectionHeading}
        </div>
        <div className='image-override-section__help'>
          {i18n.ink.notification.imageUrl.sectionHelp}
        </div>
        <Form.Group controlId='formImageUrl' className='image-url-group'>
          <div className='image-url-row'>
            <div className='image-url-thumb'>
              {!hasEffective ? (
                <div className='image-url-thumb__fallback'>
                  {i18n.ink.notification.imageUrl.noImage}
                </div>
              ) : thumbnailFailed ? (
                <div className='image-url-thumb__fallback'>
                  {i18n.ink.notification.imageUrl.previewFailed}
                </div>
              ) : (
                <img
                  src={thumbnailSrc}
                  alt='Push notification thumbnail'
                  onError={handleImageError}
                />
              )}
            </div>
            <div className='image-url-field'>
              <Form.Label className='image-url-field__label'>
                {i18n.ink.notification.imageUrl.label}
              </Form.Label>
              <Form.Control
                value={value}
                onChange={onUrlChange}
                placeholder={i18n.ink.notification.imageUrl.placeholder}
                isInvalid={!isCustomImageUrlValid}
              />
              <Form.Control.Feedback type='invalid'>
                {i18n.ink.notification.imageUrl.invalidUrl}
              </Form.Control.Feedback>
            </div>
          </div>
        </Form.Group>
      </section>
      <Form.Group controlId='includeImage' className='include-image'>
        <div className='include-image__row'>
          <Form.Check
            type='checkbox'
            label={i18n.ink.notification.includeImage.label}
            onChange={onIncludeImageChange}
            checked={hasEffective && isIncludeImage}
            disabled={!hasEffective}
          />
          <OverlayTrigger
            placement='right'
            overlay={
              <Tooltip id='include-image-tooltip'>
                {i18n.ink.notification.imageUrl.includeTooltip}
              </Tooltip>
            }
          >
            <button
              type='button'
              className='include-image__help-icon'
              aria-label='What does this do?'
              aria-describedby='include-image-tooltip'
            >
              &#9432;
            </button>
          </OverlayTrigger>
        </div>
      </Form.Group>
    </>
  );
};

export default memo(CustomImageField);
